import {
  logViewJobInterviewPrepCTAPressed,
  logViewJobInterviewPrepCTAShown,
} from "../../../../../../interview-prep/logging/cta";
import { Box, Button, Flex, Heading, Image } from "../../../../../../mads";
import { LinearGradient } from "../../../../../../shared/components/LinearGradient/linear-gradient";
import { useI18n } from "../../../../../../shared/I18n";
import { JSTLandingScreen } from "../../../../../../shared/native-modules/tare/JSTLandingScreen";
import { logScreenViewWithExtraParams } from "../../../../../../shared/native-modules/tare/logScreenViewWithExtraParams";
import { logTapButtonWithExtraParams } from "../../../../../../shared/native-modules/tare/logTapButtonWithExtraParams";
import navigateTo from "../../../../../../shared/native-modules/tare/navigateTo";
import { useViewjobOneGraphModel } from "../../../../context/ViewJobDataContext";

import { borderRadius, spacing, colors } from "@indeed/ifl-tokens/react-native";
import React, { useEffect } from "react";
import { StyleSheet, type ImageSourcePropType } from "react-native";

const ctaIconBlue = require("./images/ctaIcon-blue.png") as ImageSourcePropType;

const ctaIconYellow = require("./images/ctaIcon-yellow.png") as ImageSourcePropType;

const screenName = "rn-viewjob-interview-prep-cta";
const tags = ["interviewPrep", "viewjob"];

export const MockInterviewCTA: React.FunctionComponent = () => {
  const i18n = useI18n();
  const vjModel = useViewjobOneGraphModel();
  const isVjInterviewPrepTstBlueBg = vjModel?.features?.isVjInterviewPrepTstBlueBg ?? false;
  const isVjInterviewPrepTstYellowBg = vjModel?.features?.isVjInterviewPrepTstYellowBg ?? false;
  const showPromo = isVjInterviewPrepTstBlueBg || isVjInterviewPrepTstYellowBg;
  const occupationSuid =
    getFirstOccupationSuid(vjModel?.job.jobOccupations) || "GENERALOCCUPATIONS";

  useEffect(() => {
    if (showPromo) {
      // TODO Keep the existing logging in Android for consistency, and remove it once the CTA is fully rolled out
      void logViewJobInterviewPrepCTAShown(occupationSuid);
      void logScreenViewWithExtraParams(screenName, tags, {
        occupationSuid,
      });
    }
  }, [showPromo, occupationSuid]);

  if (!showPromo) {
    return null;
  }

  const handleCTAButtonClick = () => {
    void navigateTo(
      JSTLandingScreen.interviewPrep(occupationSuid, undefined, {
        jobKey: vjModel?.job.jobKey,
        jobTitle: vjModel?.job.jobTitle,
      }),
      "viewjob",
    );

    // TODO Keep the existing logging in Android for consistency, and remove it once the CTA is fully rolled out
    void logViewJobInterviewPrepCTAPressed(occupationSuid);
    void logTapButtonWithExtraParams(screenName, "cta_button", tags, {
      occupationSuid,
    });
  };

  return decorateBackground(
    <>
      <Heading level={4}>
        {i18n.gettext("Practice interviewing for this role with tailored questions.")}
      </Heading>
      <Flex sx={styles.bottomContainer}>
        <Box sx={styles.buttonContainer}>
          <Button variant="secondary" size="md" onPress={handleCTAButtonClick}>
            {i18n.gettext("Try a Mock Interview")}
          </Button>
        </Box>
        <Box sx={styles.iconContainer}>
          <Image
            style={styles.icon}
            source={isVjInterviewPrepTstBlueBg ? ctaIconBlue : ctaIconYellow}
            accessible={false}
            accessibilityElementsHidden={true}
            importantForAccessibility="no-hide-descendants"
          />
        </Box>
      </Flex>
    </>,
    isVjInterviewPrepTstBlueBg,
  );
};

function getFirstOccupationSuid(jobOccupations?: string[]) {
  if (jobOccupations && jobOccupations.length > 0) {
    return jobOccupations[0];
  }

  return undefined;
}

function decorateBackground(
  children: React.ReactElement,
  isVjInterviewPrepTstBlueBg: boolean,
): React.ReactElement {
  if (isVjInterviewPrepTstBlueBg) {
    return (
      <LinearGradient
        colors={[colors.utilityAiWeakStart, colors.utilityAiWeakEnd]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ margin: spacing.s4, borderRadius: borderRadius.md }}
      >
        <Box sx={{ paddingHorizontal: 20, paddingVertical: spacing.s4 }}>{children}</Box>
      </LinearGradient>
    );
  } else {
    return (
      <Box sx={[styles.container, { backgroundColor: colors.expressiveEarth200 }]}>{children}</Box>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    margin: spacing.s4,
    paddingHorizontal: 20,
    paddingVertical: spacing.s4,
    borderRadius: borderRadius.md,
  },
  bottomContainer: {
    position: "relative",
    marginTop: spacing.s5,
  },
  buttonContainer: {
    maxWidth: 220,
    zIndex: 1,
  },
  iconContainer: {
    position: "absolute",
    right: -2,
    bottom: -spacing.s4,
  },
  icon: {
    width: 107,
    height: 71,
  },
});
